/**
 * Aufrufe des CRM.
 *
 * Fuenf Bestaende in einer Datei, weil sie zusammen die Kundenakte ergeben.
 * Fuenf Dateien mit je vier gleichartigen Aufrufen waeren schwerer zu
 * ueberblicken als eine mit zwanzig.
 */

import type { components, operations } from '../../api/schema';
import { request, upload } from '../../lib/apiClient';

export type Customer = components['schemas']['CustomerRead'];
export type CustomerListItem = components['schemas']['CustomerListItem'];
export type CustomerList = components['schemas']['CustomerList'];
export type CustomerCreate = components['schemas']['CustomerCreate'];
export type CustomerUpdate = components['schemas']['CustomerUpdate'];
export type CustomerStatus = components['schemas']['CustomerStatus'];
export type CustomerFile = components['schemas']['CustomerFile'];
export type TimelineEntry = components['schemas']['TimelineEntry'];

export type Site = components['schemas']['SiteRead'];
export type SiteCreate = components['schemas']['SiteCreate'];
export type SiteUpdate = components['schemas']['SiteUpdate'];

export type Contact = components['schemas']['ContactRead'];
export type ContactLookupItem = components['schemas']['ContactLookupItem'];
export type ContactCreate = components['schemas']['ContactCreate'];
export type ContactUpdate = components['schemas']['ContactUpdate'];
export type AnonymizeResult = components['schemas']['AnonymizeResult'];

export type Contract = components['schemas']['ContractRead'];
export type ContractCreate = components['schemas']['ContractCreate'];
export type ContractUpdate = components['schemas']['ContractUpdate'];
export type ContractType = components['schemas']['ContractType'];
export type ContractStatus = components['schemas']['ContractStatus'];

export type Activity = components['schemas']['ActivityRead'];
export type ActivityCreate = components['schemas']['ActivityCreate'];
export type ActivityType = components['schemas']['ActivityType'];

export type ImportPreview = components['schemas']['ImportPreview'];
export type ImportResult = components['schemas']['ImportResult'];

export type CustomerListQuery = NonNullable<operations['listCustomers']['parameters']['query']>;

// -- Kunden -------------------------------------------------------------------

export function listCustomers(query: CustomerListQuery): Promise<CustomerList> {
  return request<CustomerList>('/customers', { query });
}

export type SuggestionField = operations['suggestValues']['parameters']['query']['field'];

/** Bisher erfasste Werte eines Freitextfelds -- haeufigste zuerst. */
export function suggestValues(field: SuggestionField, q: string): Promise<string[]> {
  return request<string[]>('/value-suggestions', { query: { field, q } });
}

/** Ansprechpartner ueber alle Kunden -- fuer Nachschlagefelder. */
export function searchContacts(q: string): Promise<ContactLookupItem[]> {
  return request<ContactLookupItem[]>('/contacts', { query: { q } });
}

/** Ein Kunde -- fuer die Anzeige einer Kennung als Name. */
export function getCustomer(customerId: string): Promise<Customer> {
  return request<Customer>(`/customers/${customerId}`);
}

/** Ein Ansprechpartner -- fuer die Anzeige einer Kennung als Name. */
export function getContact(contactId: string): Promise<Contact> {
  return request<Contact>(`/contacts/${contactId}`);
}

export function getCustomerFile(customerId: string): Promise<CustomerFile> {
  return request<CustomerFile>(`/customers/${customerId}/file`);
}

export function getCustomerTimeline(customerId: string): Promise<TimelineEntry[]> {
  return request<TimelineEntry[]>(`/customers/${customerId}/timeline`);
}

export function createCustomer(payload: CustomerCreate): Promise<Customer> {
  return request<Customer>('/customers', { method: 'POST', body: payload });
}

export function updateCustomer(customerId: string, payload: CustomerUpdate): Promise<Customer> {
  return request<Customer>(`/customers/${customerId}`, { method: 'PATCH', body: payload });
}

export function deleteCustomer(customerId: string): Promise<undefined> {
  return request<undefined>(`/customers/${customerId}`, { method: 'DELETE' });
}

// -- Standorte ----------------------------------------------------------------

export function createSite(customerId: string, payload: SiteCreate): Promise<Site> {
  return request<Site>(`/customers/${customerId}/sites`, { method: 'POST', body: payload });
}

export function updateSite(siteId: string, payload: SiteUpdate): Promise<Site> {
  return request<Site>(`/sites/${siteId}`, { method: 'PATCH', body: payload });
}

export function deleteSite(siteId: string): Promise<undefined> {
  return request<undefined>(`/sites/${siteId}`, { method: 'DELETE' });
}

// -- Ansprechpartner ----------------------------------------------------------

export function createContact(customerId: string, payload: ContactCreate): Promise<Contact> {
  return request<Contact>(`/customers/${customerId}/contacts`, { method: 'POST', body: payload });
}

export function updateContact(contactId: string, payload: ContactUpdate): Promise<Contact> {
  return request<Contact>(`/contacts/${contactId}`, { method: 'PATCH', body: payload });
}

export function deleteContact(contactId: string): Promise<undefined> {
  return request<undefined>(`/contacts/${contactId}`, { method: 'DELETE' });
}

/**
 * Entfernt den Personenbezug eines Kontakts -- unumkehrbar.
 *
 * Die Oberflaeche fragt vorher nach. Das ist hier keine Hoeflichkeit: Es gibt
 * keinen Weg zurueck, und der Aufruf ist einen Klick vom gewoehnlichen
 * Loeschen entfernt.
 */
export function anonymizeContact(contactId: string): Promise<AnonymizeResult> {
  return request<AnonymizeResult>(`/contacts/${contactId}/actions/anonymize`, { method: 'POST' });
}

// -- Vertraege ----------------------------------------------------------------

export function createContract(customerId: string, payload: ContractCreate): Promise<Contract> {
  return request<Contract>(`/customers/${customerId}/contracts`, {
    method: 'POST',
    body: payload,
  });
}

export function updateContract(contractId: string, payload: ContractUpdate): Promise<Contract> {
  return request<Contract>(`/contracts/${contractId}`, { method: 'PATCH', body: payload });
}

export function deleteContract(contractId: string): Promise<undefined> {
  return request<undefined>(`/contracts/${contractId}`, { method: 'DELETE' });
}

export function checkContractExpiry(leadDays: number): Promise<Contract[]> {
  return request<Contract[]>('/contracts/actions/check-expiry', {
    method: 'POST',
    query: { lead_days: leadDays },
  });
}

// -- Aktivitaeten -------------------------------------------------------------

export function createActivity(customerId: string, payload: ActivityCreate): Promise<Activity> {
  return request<Activity>(`/customers/${customerId}/activities`, {
    method: 'POST',
    body: payload,
  });
}

export function deleteActivity(activityId: string): Promise<undefined> {
  return request<undefined>(`/activities/${activityId}`, { method: 'DELETE' });
}

// -- Import -------------------------------------------------------------------

export function previewCustomerImport(file: File): Promise<ImportPreview> {
  return upload<ImportPreview>('/customers/actions/import-preview', file);
}

export function importCustomers(file: File): Promise<ImportResult> {
  return upload<ImportResult>('/customers/actions/import', file);
}

export function previewContactImport(file: File): Promise<ImportPreview> {
  return upload<ImportPreview>('/contacts/actions/import-preview', file);
}

export function importContacts(file: File): Promise<ImportResult> {
  return upload<ImportResult>('/contacts/actions/import', file);
}
